import React from 'react';
import {
  Form,
  Link,
  useActionData,
  useLoaderData,
  useNavigate,
  useNavigation,
  useOutlet,
  useRevalidator,
  useSubmit
} from 'react-router-dom';
import * as Headless from '@headlessui/react';
import { AnimatePresence } from 'motion/react';
import { ChevronRightIcon, EllipsisVerticalIcon, UserGroupIcon } from '@heroicons/react/24/outline';

import { Await } from '@oysterjs/ui/async';
import { Badge } from '@oysterjs/ui/badge';
import { Button } from '@oysterjs/ui/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
  MountableDialog
} from '@oysterjs/ui/dialog';
import {
  Description,
  ErrorMessage,
  Field,
  FieldGroup,
  Fieldset,
  Label
} from '@oysterjs/ui/fieldset';
import { Input } from '@oysterjs/ui/input';
import { Spinner } from '@oysterjs/ui/spinner';
import { Textarea } from '@oysterjs/ui/textarea';
import { ActionResult } from '@oysterjs/core/graphql/actions';
import { useFormActions } from '@oysterjs/core/graphql/form';
import { ValidationError } from '@oysterjs/core/graphql';

import {
  Action,
  Resource,
  SubscriptionEntitlement,
  useArchiveAgencyProfileMutation,
  type AgencyProfileSummaryFieldsFragment,
  type CreateAgencyProfileInput,
  type CreateAgencyProfileMutation
} from '~/api/graphql';
import { useEntitlements } from '~/components/entitlements';
import { PermissionButton, PermissionNotice, usePermissions } from '~/components/permissions';
import { EnterpriseUpgradeNotice } from '~/components/settings/enterprise-upgrade-notice';

const CREATE_AGENCY_PROFILE = [{ resource: Resource.AgencyProfile, action: Action.Create }];

const DOCUMENTATION_URL = 'https://docs.getcara.ai/admin/profiles';

/** Reads like the Voice AI banner: what the feature is for, and where to learn more. */
const AgencyProfilesStatusBanner = () => (
  <div className="overflow-hidden rounded-lg bg-gradient-to-r from-sky-700 to-sky-600">
    <div className="px-4 py-5 sm:p-6">
      <div className="flex items-center justify-between">
        <h3 className="m-0 p-0 text-lg font-medium text-neutral-50">
          Get started with access profiles
        </h3>
        <Button color="white" href={DOCUMENTATION_URL} target="_blank">
          View Documentation
        </Button>
      </div>
      <p className="mt-2 text-sm text-neutral-200">
        Build a profile for each way your agency works, assign it to users and teams, and Cara shows
        each of them only what they need.
      </p>
    </div>
  </div>
);

/** "3 people · 2 teams", or a nudge when nothing is assigned. */
const assignmentLabel = (profile: AgencyProfileSummaryFieldsFragment) => {
  const parts: string[] = [];
  if (profile.assignedUserCount > 0) {
    parts.push(
      `${profile.assignedUserCount} ${profile.assignedUserCount === 1 ? 'person' : 'people'}`
    );
  }
  if (profile.teams.length > 0) {
    parts.push(`${profile.teams.length} ${profile.teams.length === 1 ? 'team' : 'teams'}`);
  }
  return parts.length > 0 ? parts.join(' · ') : 'Not assigned';
};

/**
 * One row in the list. The row menu matches Settings › Teams, which is the
 * other place an admin archives something they created here.
 */
const AgencyProfileRow = ({
  profile,
  canArchive,
  onArchive
}: {
  profile: AgencyProfileSummaryFieldsFragment;
  canArchive: boolean;
  onArchive: () => void;
}) => (
  <div className="flex items-center justify-between gap-4">
    <Link
      to={`/app/settings/access-profiles/${profile.id}`}
      className="flex min-w-0 flex-1 items-center justify-between hover:cursor-pointer"
    >
      <div className="min-w-0">
        <h4 className="m-0 flex flex-wrap items-center gap-2 p-0 font-body text-base font-medium text-neutral-900 dark:text-white">
          {profile.name}
          {profile.isDefault && <Badge color="sky">Default</Badge>}
          <Badge color="zinc">{assignmentLabel(profile)}</Badge>
        </h4>
        <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">{profile.description}</p>
      </div>
      <ChevronRightIcon
        aria-hidden="true"
        className="size-5 flex-none text-neutral-400 dark:text-neutral-500"
      />
    </Link>
    {canArchive && (
      <Headless.Menu as="div" className="relative flex-none">
        <Headless.MenuButton className="-m-2.5 block p-2.5 text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white">
          <span className="sr-only">Open options for {profile.name}</span>
          <EllipsisVerticalIcon aria-hidden="true" className="size-5" />
        </Headless.MenuButton>
        <Headless.MenuItems
          transition
          className="absolute right-0 z-10 mt-2 w-32 origin-top-right rounded-md bg-white dark:bg-neutral-800 py-2 shadow-lg ring-1 ring-neutral-900/5 dark:ring-white/10 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
        >
          <Headless.MenuItem>
            <span
              role="button"
              onClick={onArchive}
              className="block px-3 py-1 text-sm/6 text-neutral-900 dark:text-white data-[focus]:bg-neutral-50 dark:data-[focus]:bg-white/5 data-[focus]:outline-none"
            >
              Archive<span className="sr-only">, {profile.name}</span>
            </span>
          </Headless.MenuItem>
        </Headless.MenuItems>
      </Headless.Menu>
    )}
  </div>
);

export const SettingsAgencyProfilesPage = () => {
  const outlet = useOutlet();
  const { hasEntitlement } = useEntitlements();
  const { hasPermission } = usePermissions();
  const revalidator = useRevalidator();
  const loader = useLoaderData() as {
    data: Promise<{ agencyProfiles: AgencyProfileSummaryFieldsFragment[] }>;
  };

  const [archiveProfile] = useArchiveAgencyProfileMutation();
  const [archiving, setArchiving] = React.useState<AgencyProfileSummaryFieldsFragment | null>(null);
  const [archiveLoading, setArchiveLoading] = React.useState(false);
  const [archiveError, setArchiveError] = React.useState<string>();

  const canArchive = hasPermission(Resource.AgencyProfile, Action.Delete);

  if (!hasEntitlement(SubscriptionEntitlement.AgencyProfileManagement)) {
    return (
      <EnterpriseUpgradeNotice
        title="Access Profiles"
        icon={UserGroupIcon}
        heading="Access profiles are available on the Enterprise plan"
        documentationUrl={DOCUMENTATION_URL}
      >
        A profile is a saved configuration you assign to users and teams. It decides which tabs they
        can open, what their home screen looks like, and what they are allowed to do.
      </EnterpriseUpgradeNotice>
    );
  }

  return (
    <>
      <React.Suspense fallback={<AgencyProfilesPageLoader />}>
        <Await resolve={loader.data}>
          {(data) =>
            data && (
              <div className="space-y-6">
                <AgencyProfilesStatusBanner />
                <div className="divide-y divide-neutral-200 dark:divide-neutral-700 rounded-lg bg-white dark:bg-neutral-800 shadow">
                  <div className="px-4 py-5 sm:px-6">
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <h3 className="m-0 p-0 text-lg font-medium text-neutral-900 dark:text-white">
                          Access Profiles
                        </h3>
                        <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
                          Each user belongs to exactly one profile. Unassigned users fall back to
                          the default.
                        </p>
                      </div>
                      {!!data.agencyProfiles.length && (
                        <PermissionButton
                          permissions={CREATE_AGENCY_PROFILE}
                          color="sky"
                          href="/app/settings/access-profiles/new"
                        >
                          Create Profile
                        </PermissionButton>
                      )}
                    </div>
                  </div>
                  <div className="px-4 py-5 sm:px-6">
                    {!data.agencyProfiles.length ? (
                      <div className="py-12 text-center">
                        <UserGroupIcon className="mx-auto size-12 text-neutral-400 dark:text-neutral-500" />
                        <h3 className="mt-2 text-sm font-semibold text-neutral-900 dark:text-white">
                          No profiles
                        </h3>
                        <p className="mx-auto mt-1 max-w-xl text-sm text-neutral-500 dark:text-neutral-400">
                          You haven&rsquo;t created any profiles yet. Everyone at your agency
                          currently sees the full app. Create your first profile to get started.
                        </p>
                        <div className="mt-4">
                          <PermissionButton
                            permissions={CREATE_AGENCY_PROFILE}
                            color="sky"
                            href="/app/settings/access-profiles/new"
                          >
                            Create Profile
                          </PermissionButton>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-6">
                        {data.agencyProfiles.map((profile) => (
                          <AgencyProfileRow
                            key={profile.id}
                            profile={profile}
                            canArchive={canArchive}
                            onArchive={() => {
                              setArchiveError(undefined);
                              setArchiving(profile);
                            }}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )
          }
        </Await>
      </React.Suspense>
      <AnimatePresence mode="wait">{outlet && React.cloneElement(outlet)}</AnimatePresence>

      <Dialog open={!!archiving} onClose={() => setArchiving(null)}>
        <DialogTitle>Archive {archiving?.name}</DialogTitle>
        <DialogDescription>
          Everyone on this profile falls back to your agency&rsquo;s default profile, or to whatever
          their role allows if you have no default. The profile stops appearing in this list.
        </DialogDescription>
        {archiveError && (
          <DialogBody>
            <ErrorMessage>{archiveError}</ErrorMessage>
          </DialogBody>
        )}
        <DialogActions>
          <Button color="white" disabled={archiveLoading} onClick={() => setArchiving(null)}>
            Cancel
          </Button>
          <Button
            color="sky"
            loading={archiveLoading}
            onClick={async () => {
              if (!archiving) {
                return;
              }
              setArchiveLoading(true);
              setArchiveError(undefined);
              try {
                await archiveProfile({ variables: { id: archiving.id } });
                setArchiving(null);
                // The list comes from the route loader, not the Apollo cache,
                // so the mutation result alone would leave the archived row up.
                revalidator.revalidate();
              } catch (err) {
                setArchiveError(err instanceof Error ? err.message : 'Failed to archive profile');
              } finally {
                setArchiveLoading(false);
              }
            }}
          >
            Archive profile
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const AgencyProfilesPageLoader = () => (
  <div className="rounded-lg bg-white dark:bg-neutral-800 px-6 shadow">
    <div className="py-12 text-center">
      <Spinner size="size-8" color="neutral" />
      <h3 className="mt-2 text-sm font-semibold text-neutral-900 dark:text-white">
        Loading profiles...
      </h3>
      <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
        This might take a moment.
      </p>
    </div>
  </div>
);

export const SettingsAgencyProfileCreatePage = () => {
  const navigate = useNavigate();
  const navigation = useNavigation();
  const submit = useSubmit();
  const action = useActionData() as ActionResult<CreateAgencyProfileMutation>;

  // Reachable by URL, so the dialog checks rather than trusting the button on
  // the list page behind it.
  const canCreate = usePermissions().hasAllPermissions(CREATE_AGENCY_PROFILE);

  const [validationErrors, setValidationErrors] = React.useState<ValidationError[]>([]);
  const [form, setForm] = React.useState<CreateAgencyProfileInput>({ name: '', description: '' });
  const { updateForm, getValidationError } = useFormActions(setForm, setValidationErrors);
  // Synchronous guard against rapid double-submits before navigation.state updates.
  const submittingRef = React.useRef(false);

  // The server decides what is valid — required fields, length caps, and the
  // name collision it is the only one who can see — and answers with a field
  // path per problem. Submitting unconditionally is what lets all of them
  // arrive at once instead of one round-trip at a time.
  React.useEffect(() => {
    if (action?.validationErrors) {
      setValidationErrors(action.validationErrors);
    }
  }, [action?.validationErrors]);

  React.useEffect(() => {
    if (navigation.state === 'idle') {
      submittingRef.current = false;
    }
  }, [navigation.state]);

  const nameError = getValidationError(validationErrors, ['name']);
  const descriptionError = getValidationError(validationErrors, ['description']);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (submittingRef.current) {
      return;
    }
    submittingRef.current = true;
    submit(form, { method: 'POST', encType: 'application/json' });
  };

  return (
    <MountableDialog onClose={() => navigate(-1)} size="md" className="relative">
      <DialogTitle>Create a new profile</DialogTitle>
      <DialogDescription>Define what users can see and do in the app.</DialogDescription>
      <Form onSubmit={handleSubmit}>
        <DialogBody>
          {!canCreate && (
            <PermissionNotice className="mb-6">
              You don&rsquo;t have permission to create profiles.
            </PermissionNotice>
          )}
          {action?.applicationError && (
            <ErrorMessage className="mb-6">{action.applicationError.message}</ErrorMessage>
          )}
          <Fieldset disabled={!canCreate}>
            <FieldGroup>
              <Field>
                <Label>Name</Label>
                <Description>
                  A brief, descriptive name for the profile. Name it based on the permissions you
                  want to grant or the group you want to manage.
                </Description>
                <Input
                  value={form.name}
                  placeholder="Producers"
                  invalid={!!nameError}
                  onChange={(e) => {
                    const name = e.currentTarget.value;
                    updateForm(['name'], (prev) => ({ ...prev, name }));
                  }}
                />
                {!!nameError && <ErrorMessage>{nameError}</ErrorMessage>}
              </Field>
              <Field>
                <Label>Description</Label>
                <Description>
                  Who this profile is for and what they can do. Only admins see this.
                </Description>
                <Textarea
                  rows={3}
                  value={form.description}
                  placeholder="Sales-facing view for producers who quote and bind."
                  invalid={!!descriptionError}
                  onChange={(e) => {
                    const description = e.currentTarget.value;
                    updateForm(['description'], (prev) => ({ ...prev, description }));
                  }}
                />
                {!!descriptionError && <ErrorMessage>{descriptionError}</ErrorMessage>}
              </Field>
            </FieldGroup>
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <Button
            type="button"
            color="white"
            disabled={navigation.state === 'submitting'}
            onClick={() => navigate(-1)}
          >
            Close
          </Button>
          <Button type="submit" color="sky" loading={navigation.state === 'submitting'}>
            Create Profile
          </Button>
        </DialogActions>
      </Form>
    </MountableDialog>
  );
};
